import { useState, useEffect, useCallback } from 'react';
import { perfilNoModulo } from '../../../utils/perfisDerivados';
import {
  Filter, ChevronRight, ChevronDown, ArrowUp, ArrowDown,
  ClipboardList, CalendarRange, CalendarDays, Info,
} from 'lucide-react';

// =============================================================================
// Medicina do Trabalho — Consulta de Atestados.
//
// Substitui o antigo relatório linha a linha E a página "Revisões Atestados"
// do BI MCDES_16 — o portal passa a ser a fonte única desse número.
//
// A leitura é sempre em PAR: Total e "mais de 1 dia". Atestado de 1 dia é
// difícil de atuar; o valor da revisão do médico do trabalho está nos de
// vários dias, e é por isso que a coluna ">1 dia" existe em todo indicador.
//
// O recorte por loja é decidido NO SERVIDOR pelo token — o gerente vê só a
// própria loja mesmo que mexa na URL. Aqui o seletor de loja só aparece para
// quem o servidor deixa escolher.
// =============================================================================

// Paleta do módulo Medicina do Trabalho (verde) — ver `docs/ARQUITETURA.md` ›
// Paleta dos módulos. Tela nova NÃO inventa cor: usa a da casa dela.
const VERDE = '#16a34a';
const VERDE_ESCURO = '#15803d';
const VERDE_CLARO = '#dcfce7';  // fundo do aviso de recorte
const TINTA = '#0f172a';   // texto normal — nunca cinza claro em número/rótulo
// Azul do card "Todos os atestados". A coluna "% redução" da matriz usa o
// MESMO azul: é a mesma medida, no mesmo recorte — a cor amarra as duas.
const AZUL = '#3b82f6';
const VERDE_REDUCAO = '#10b981';  // a redução, nos três cenários

// Perfis que enxergam a rede (e portanto podem escolher a loja). O servidor é
// quem manda de verdade; isto é só para não mostrar um seletor inócuo ao gerente.
// `Manutencao` está aqui porque também é do ESCRITÓRIO (vê as 13 lojas) — o
// que o servidor recorta para ela é o CARGO, não a loja.
const VE_A_REDE = ['Admin', 'Diretoria', 'RH', 'Medico', 'Manutencao'];

// As duas leituras possíveis do período. São recortes DIFERENTES do mesmo dado
// (85% dos atestados são lançados num dia distinto do que está escrito neles),
// então o total muda conforme o critério — e é por isso que ele é explícito na
// tela em vez de fixo: quando dois números não batem, a tela mostra o porquê.
const BASES_PERIODO = [
  {
    valor: 'atestado',
    rotulo: 'Data do atestado',
    de: 'Atestado de', ate: 'Atestado até',
    nota: 'O período é a data escrita no documento — é assim que se analisa o mês fechado, porque o número não muda depois.',
  },
  {
    valor: 'lancamento',
    rotulo: 'Data do lançamento',
    de: 'Lançado de', ate: 'Lançado até',
    nota: 'O período é a data em que o médico registrou no portal — é o trabalho de revisão dele. Um atestado antigo lançado hoje entra no período de hoje.',
  },
];

// Default por perfil: o médico abre no que ele acabou de lançar; todo o resto
// abre no mês do atestado, que é a leitura de gestão. Só o DEFAULT muda — todo
// mundo pode trocar, senão o médico não conseguiria conferir o mesmo número
// que a Diretoria está vendo.
const basePadrao = (perfil) => (perfil === 'Medico' ? 'lancamento' : 'atestado');

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('pt-BR'));

// Percentual: null vira "—". É o caso "não houve atestado no recorte" — o BI
// mostra 100% aqui por causa do DIVIDE com alternativa 0, e isso é enganoso.
const fmtPct = (v) => (v == null ? '—' : `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`);

// Mês corrente: do dia 1 até HOJE (nunca até o fim do mês — dia futuro não tem
// atestado e a tela prometeria um período que ainda não aconteceu).
const doisDigitos = (n) => String(n).padStart(2, '0');
const primeiroDiaDoMes = () => {
  const h = new Date();
  return `${h.getFullYear()}-${doisDigitos(h.getMonth() + 1)}-01`;
};
const hojeISO = () => {
  const h = new Date();
  return `${h.getFullYear()}-${doisDigitos(h.getMonth() + 1)}-${doisDigitos(h.getDate())}`;
};

// Colunas da matriz — uma fonte só para o cabeçalho, a ordenação e as células.
const COLUNAS = [
  { chave: 'rotulo', titulo: 'Loja / Cargo / Nome', tipo: 'texto' },
  { chave: 'qtd', titulo: 'Atestados', tipo: 'numero' },
  { chave: 'diasSolicitados', titulo: 'Dias solic.', tipo: 'numero' },
  { chave: 'diasConcedidos', titulo: 'Dias conc.', tipo: 'numero' },
  { chave: 'reducao', titulo: '% redução', tipo: 'pct' },
  { chave: 'qtdMais1', titulo: 'Atestados >1d', tipo: 'numero', separa: true },
  { chave: 'diasSolicitadosMais1', titulo: 'Dias solic. >1d', tipo: 'numero' },
  { chave: 'diasConcedidosMais1', titulo: 'Dias conc. >1d', tipo: 'numero' },
  { chave: 'reducaoMais1', titulo: '% redução >1d', tipo: 'pct' },
];

/**
 * Ordena os filhos de um nó, recursivamente. Texto sai alfabético (pt-BR, para
 * acento não jogar "Ávila" pro fim); número e percentual saem pelo valor.
 * Nulo vai sempre para o fim, nos dois sentidos — "sem dado" não é o menor
 * valor, é ausência de valor, e vê-lo no topo do maior→menor confunde.
 */
const ordenarNos = (nos, coluna, desc) => {
  const texto = COLUNAS.find((c) => c.chave === coluna)?.tipo === 'texto';
  const copia = [...nos].sort((a, b) => {
    // `ordenacao` existe só onde o rótulo não serve para ordenar: a linha do
    // atestado mostra dd/mm/aaaa, que como texto poria 02/08 antes de 10/07.
    // O servidor manda o ISO nesse campo.
    if (texto) {
      const ta = a.ordenacao || a[coluna];
      const tb = b.ordenacao || b[coluna];
      return String(ta ?? '').localeCompare(String(tb ?? ''), 'pt-BR');
    }
    const va = a[coluna];
    const vb = b[coluna];
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    return va - vb;
  });
  if (desc) copia.reverse();
  return copia.map((no) => (no.filhos?.length ? { ...no, filhos: ordenarNos(no.filhos, coluna, desc) } : no));
};

const MedicinaAtestadosConsulta = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const podeEscolherLoja = VE_A_REDE.includes(perfilNoModulo(user?.perfil, 'medicina'));

  const [inicio, setInicio] = useState(primeiroDiaDoMes);
  const [fim, setFim] = useState(hojeISO);
  const [base, setBase] = useState(() => basePadrao(user?.perfil));
  const [loja, setLoja] = useState('');
  const [usuario, setUsuario] = useState('');

  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [expandidos, setExpandidos] = useState(() => new Set());
  // Ordenação da matriz. Começa alfabética pela loja, que é como o servidor manda.
  const [ordem, setOrdem] = useState({ coluna: 'rotulo', desc: false });

  const buscar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({ inicio, fim, base });
      if (loja) params.set('loja', loja);
      if (usuario) params.set('usuario', usuario);

      const r = await fetch(`/api/atestados/consulta?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`Falha ao consultar (${r.status})`);
      setDados(await r.json());
    } catch (e) {
      setErro(e.message);
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, [inicio, fim, base, loja, usuario]);

  // Carga inicial apenas. Depois é o botão "Aplicar" que manda — com `buscar`
  // nas dependências, cada tecla digitada num campo de data dispararia uma
  // consulta nova (e datas passam por estados inválidos enquanto se digita).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- lista vazia de propósito
  useEffect(() => { buscar(); }, []);

  const alternar = (chave) => {
    setExpandidos((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(chave)) proximo.delete(chave); else proximo.add(chave);
      return proximo;
    });
  };

  // Clicar no cabeçalho ordena. Primeiro clique numa coluna nova: texto começa
  // A→Z, número começa do MAIOR (é o que se procura numa análise). Clique
  // seguinte inverte.
  const ordenarPor = (col) => {
    setOrdem((atual) => (
      atual.coluna === col.chave
        ? { coluna: col.chave, desc: !atual.desc }
        : { coluna: col.chave, desc: col.tipo !== 'texto' }
    ));
  };

  // --- Cards de indicadores ------------------------------------------------
  // Formato do StatCard dos Primeiros 15 (faixa colorida à esquerda + ícone em
  // círculo): uma métrica por card, com uma linha de apoio embaixo do número.
  // ⚠️ A paleta variada aqui é de KPI, não de identidade — a cor do MÓDULO
  // (verde) governa navegação e cromo; cards de número podem diferenciar
  // métrica por cor, como no RH. Ver docs/ARQUITETURA.md › Paleta dos módulos.
  // Card de CENÁRIO. Os três têm a MESMA estrutura de propósito — atestados,
  // dias solicitados → concedidos e a redução — mudando só o recorte. É a
  // simetria que torna a comparação imediata: lidos lado a lado, eles mostram
  // onde a revisão médica rende e onde ela quase não tem o que cortar.
  //
  // A redução é o número grande porque é a única das três medidas que se
  // compara direto entre cenários de tamanhos diferentes (contagem e dias
  // variam com o volume; o percentual, não).
  const CardCenario = ({ titulo, atestados, pctDoTotal, solicitados, concedidos, reducao, icone: Icone, cor, corFundo }) => (
    <div style={{
      backgroundColor: '#fff', padding: 20, borderRadius: 16, border: '1px solid #e2e8f0',
      borderLeft: `4px solid ${cor}`, display: 'flex', alignItems: 'flex-start',
      justifyContent: 'space-between', gap: 16, flex: '1 1 290px', minWidth: 290,
    }}>
      <div>
        <p style={{ color: TINTA, fontSize: 11, textTransform: 'uppercase', fontWeight: 800, margin: '0 0 6px 0' }}>{titulo}</p>
        {/* A redução é sempre VERDE nos três cards — é a mesma medida, e cor
            igual é o que deixa os cenários comparáveis de relance. A cor do
            cenário fica na faixa e no ícone, que é onde ela identifica. */}
        <p style={{ fontSize: 30, fontWeight: 800, color: VERDE_REDUCAO, margin: 0, lineHeight: 1.1 }}>{fmtPct(reducao)}</p>
        <p style={{ fontSize: 11, fontWeight: 700, color: TINTA, margin: '0 0 10px 0', textTransform: 'uppercase' }}>de redução</p>
        <p style={{ fontSize: 13, fontWeight: 700, color: TINTA, margin: 0 }}>
          {fmtNum(atestados)} atestado{atestados === 1 ? '' : 's'}
          {pctDoTotal != null && <span style={{ color: cor }}> ({fmtPct(pctDoTotal)} do total)</span>}
        </p>
        <p style={{ fontSize: 13, fontWeight: 700, color: TINTA, margin: '2px 0 0 0' }}>
          {fmtNum(solicitados)} dias pedidos → {fmtNum(concedidos)} concedidos
        </p>
      </div>
      <div style={{ padding: 12, borderRadius: '50%', backgroundColor: corFundo, color: cor, flexShrink: 0 }}>
        <Icone size={22} />
      </div>
    </div>
  );

  // --- Uma linha da matriz (loja, cargo ou pessoa) ------------------------
  const Linha = ({ no, nivel, caminho }) => {
    const chave = `${caminho}/${no.chave}`;
    const temFilhos = no.filhos && no.filhos.length > 0;
    const aberto = expandidos.has(chave);

    const celula = { padding: '10px 16px', fontSize: 14, color: TINTA, textAlign: 'right', whiteSpace: 'nowrap' };

    return (
      <>
        <tr style={{
          borderBottom: '1px solid #f1f5f9',
          backgroundColor: nivel === 0 ? '#f8fafc' : '#fff',
          fontWeight: nivel === 0 ? 700 : 400,
        }}>
          <td style={{ ...celula, textAlign: 'left', paddingLeft: 16 + nivel * 24 }}>
            <span
              onClick={() => temFilhos && alternar(chave)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: temFilhos ? 'pointer' : 'default' }}
            >
              {temFilhos
                ? (aberto ? <ChevronDown size={15} color={TINTA} /> : <ChevronRight size={15} color={TINTA} />)
                : <span style={{ width: 15 }} />}
              {no.rotulo}
            </span>
          </td>
          <td style={celula}>{fmtNum(no.qtd)}</td>
          <td style={celula}>{fmtNum(no.diasSolicitados)}</td>
          <td style={celula}>{fmtNum(no.diasConcedidos)}</td>
          <td style={{ ...celula, fontWeight: 700, color: no.reducao == null ? TINTA : AZUL }}>{fmtPct(no.reducao)}</td>
          <td style={{ ...celula, borderLeft: '2px solid #e2e8f0' }}>{fmtNum(no.qtdMais1)}</td>
          <td style={celula}>{fmtNum(no.diasSolicitadosMais1)}</td>
          <td style={celula}>{fmtNum(no.diasConcedidosMais1)}</td>
          <td style={{ ...celula, fontWeight: 700, color: no.reducaoMais1 == null ? TINTA : VERDE_ESCURO }}>
            {fmtPct(no.reducaoMais1)}
          </td>
        </tr>
        {aberto && no.filhos.map((filho) => (
          <Linha key={`${chave}/${filho.chave}`} no={filho} nivel={nivel + 1} caminho={chave} />
        ))}
      </>
    );
  };

  const campo = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, width: '100%', boxSizing: 'border-box', color: TINTA };
  const rotuloCampo = { display: 'block', fontSize: 12, fontWeight: 700, color: TINTA, marginBottom: 4 };
  const rodapeCel = { padding: '12px 16px', fontSize: 14, textAlign: 'right', color: TINTA };

  const total = dados?.total;
  const lojasOrdenadas = dados ? ordenarNos(dados.lojas, ordem.coluna, ordem.desc) : [];

  // Dois critérios, de propósito: `baseAtual` é o que está SELECIONADO (manda
  // nos rótulos dos campos, que descrevem o que será aplicado) e `baseAplicada`
  // é o que gerou os números na tela. Enquanto o usuário troca o seletor e não
  // clica em Aplicar, os dois divergem — e a nota embaixo da tabela precisa
  // falar da tabela que está ali, não da que virá.
  const baseAtual = BASES_PERIODO.find((b) => b.valor === base) || BASES_PERIODO[0];
  const baseAplicada = BASES_PERIODO.find((b) => b.valor === dados?.periodo?.base) || baseAtual;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: TINTA, margin: 0 }}>Consulta de Atestados</h2>
        <p style={{ color: TINTA, fontSize: 15, margin: '6px 0 0 0' }}>
          Quantos atestados foram entregues e quanto a revisão do médico do trabalho reduziu em dias.
          Abra a loja para ver por cargo, o cargo para ver por pessoa e a pessoa para ver
          <strong> cada atestado dela</strong>.
        </p>
      </div>

      {/* Aviso de recorte: quem vê um PEDAÇO da rede precisa saber disso na
          tela, senão lê o total como se fosse o da empresa inteira. Quem manda
          é o servidor — a tela só repete o que ele respondeu. */}
      {dados?.recorteCargo && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24,
          padding: '12px 16px', borderRadius: 12,
          backgroundColor: VERDE_CLARO, border: `1px solid ${VERDE}`, color: TINTA,
          fontSize: 14, fontWeight: 600,
        }}>
          <Info size={18} color={VERDE} style={{ flexShrink: 0 }} />
          <span>
            Você está vendo apenas os atestados dos cargos de <strong>{dados.recorteCargo}</strong>.
            Os totais abaixo são desse recorte, não da rede inteira.
          </span>
        </div>
      )}

      {/* FILTROS */}
      <div style={{
        backgroundColor: '#fff', padding: 20, borderRadius: 12, border: '1px solid #e2e8f0',
        display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', marginBottom: 24,
      }}>
        {/* O critério do período vem PRIMEIRO porque ele governa os dois campos
            de data à direita — e os rótulos deles mudam junto, para não sobrar
            dúvida sobre o que está sendo filtrado. */}
        <div style={{ flex: '1 1 170px' }}>
          <label style={rotuloCampo} htmlFor="base">Período por</label>
          <select id="base" value={base} onChange={(e) => setBase(e.target.value)} style={campo}>
            {BASES_PERIODO.map((b) => <option key={b.valor} value={b.valor}>{b.rotulo}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={rotuloCampo} htmlFor="inicio">{baseAtual.de}</label>
          <input id="inicio" type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} style={campo} />
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={rotuloCampo} htmlFor="fim">{baseAtual.ate}</label>
          <input id="fim" type="date" value={fim} onChange={(e) => setFim(e.target.value)} style={campo} />
        </div>

        {podeEscolherLoja && (
          <div style={{ flex: '1 1 150px' }}>
            <label style={rotuloCampo} htmlFor="loja">Loja</label>
            <select id="loja" value={loja} onChange={(e) => setLoja(e.target.value)} style={campo}>
              <option value="">Todas</option>
              {(dados?.lojas || []).map((l) => <option key={l.chave} value={l.chave}>{l.rotulo}</option>)}
            </select>
          </div>
        )}

        {/* Filtro por quem lançou — é como o médico vê "o que eu fiz". */}
        <div style={{ flex: '1 1 180px' }}>
          <label style={rotuloCampo} htmlFor="usuario">Lançado por</label>
          <select id="usuario" value={usuario} onChange={(e) => setUsuario(e.target.value)} style={campo}>
            <option value="">Todos</option>
            {(dados?.usuarios || []).map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>

        <button
          onClick={buscar}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
            backgroundColor: VERDE, color: '#fff', border: 'none', borderRadius: 8,
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          <Filter size={16} /> Aplicar
        </button>
      </div>

      {erro && (
        <div style={{ padding: 16, borderRadius: 12, backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontWeight: 600, marginBottom: 24 }}>
          {erro}
        </div>
      )}

      {carregando && <p style={{ color: TINTA, fontWeight: 600 }}>Carregando…</p>}

      {!carregando && total && (
        <>
          {/* OS TRÊS CENÁRIOS, lado a lado e com a mesma estrutura: o período
              inteiro, o recorte em que dá para atuar (>1 dia) e o que
              praticamente não dá (1 dia). Ler os três de uma vez é o ponto. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
            <CardCenario
              titulo="Todos os atestados"
              atestados={total.qtd}
              solicitados={total.diasSolicitados}
              concedidos={total.diasConcedidos}
              reducao={total.reducao}
              icone={ClipboardList} cor={AZUL} corFundo="#eff6ff"
            />
            <CardCenario
              titulo="Mais de 1 dia"
              atestados={total.qtdMais1}
              pctDoTotal={total.qtd > 0 ? total.qtdMais1 / total.qtd : null}
              solicitados={total.diasSolicitadosMais1}
              concedidos={total.diasConcedidosMais1}
              reducao={total.reducaoMais1}
              icone={CalendarRange} cor="#f59e0b" corFundo="#fffbeb"
            />
            <CardCenario
              titulo="De 1 dia"
              atestados={total.qtd1Dia}
              pctDoTotal={total.qtd > 0 ? total.qtd1Dia / total.qtd : null}
              solicitados={total.diasSolicitados1Dia}
              concedidos={total.diasConcedidos1Dia}
              reducao={total.reducao1Dia}
              icone={CalendarDays} cor="#8b5cf6" corFundo="#f5f3ff"
            />
          </div>

          {/* MATRIZ */}
          <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', color: TINTA, fontSize: 12, textTransform: 'uppercase' }}>
                    {COLUNAS.map((col) => {
                      const ativa = ordem.coluna === col.chave;
                      return (
                        <th
                          key={col.chave}
                          onClick={() => ordenarPor(col)}
                          title="Clique para ordenar"
                          style={{
                            padding: '12px 16px', fontWeight: 800, borderBottom: '1px solid #e2e8f0',
                            textAlign: col.tipo === 'texto' ? 'left' : 'right', whiteSpace: 'nowrap',
                            cursor: 'pointer', userSelect: 'none',
                            borderLeft: col.separa ? '2px solid #e2e8f0' : undefined,
                            color: ativa ? VERDE_ESCURO : TINTA,
                          }}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {col.titulo}
                            {ativa && (ordem.desc ? <ArrowDown size={13} /> : <ArrowUp size={13} />)}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {lojasOrdenadas.length === 0 && (
                    <tr><td colSpan={COLUNAS.length} style={{ padding: 24, textAlign: 'center', color: TINTA, fontWeight: 600 }}>
                      Nenhum atestado no período selecionado.
                    </td></tr>
                  )}
                  {lojasOrdenadas.map((l) => <Linha key={l.chave} no={l} nivel={0} caminho="" />)}
                </tbody>
                {lojasOrdenadas.length > 0 && (
                  <tfoot>
                    <tr style={{ backgroundColor: '#f8fafc', fontWeight: 800, borderTop: '2px solid #e2e8f0' }}>
                      <td style={{ ...rodapeCel, textAlign: 'left' }}>Total</td>
                      <td style={rodapeCel}>{fmtNum(total.qtd)}</td>
                      <td style={rodapeCel}>{fmtNum(total.diasSolicitados)}</td>
                      <td style={rodapeCel}>{fmtNum(total.diasConcedidos)}</td>
                      <td style={{ ...rodapeCel, color: AZUL }}>{fmtPct(total.reducao)}</td>
                      <td style={{ ...rodapeCel, borderLeft: '2px solid #e2e8f0' }}>{fmtNum(total.qtdMais1)}</td>
                      <td style={rodapeCel}>{fmtNum(total.diasSolicitadosMais1)}</td>
                      <td style={rodapeCel}>{fmtNum(total.diasConcedidosMais1)}</td>
                      <td style={{ ...rodapeCel, color: VERDE_ESCURO }}>{fmtPct(total.reducaoMais1)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          <p style={{ fontSize: 12, color: TINTA, fontWeight: 600, marginTop: 12 }}>
            Filtrado por <strong>{baseAplicada.rotulo.toLowerCase()}</strong>. {baseAplicada.nota}{' '}
            Abrindo o nome, cada linha é <strong>um atestado</strong>, pela data do documento — é o que
            distingue três documentos no mês de um único documento de três dias.
            Só entram registros do tipo <strong>Atestado</strong> — consultas e declarações de horas não têm dias.
            A loja é a <strong>localização</strong> do funcionário no dia do atestado, não a empresa que o contratou.
            Atestados anteriores a 28/07/2026 aparecem como <em>Sem identificação</em> no nível de pessoa:
            o nome não era guardado.
          </p>
        </>
      )}
    </div>
  );
};

export default MedicinaAtestadosConsulta;
